(() => {
  'use strict';

  const d = document;
  const w = window;
  const nav = w.navigator || {};
  const $ = (selector, context = d) => context.querySelector(selector);
  const $$ = (selector, context = d) => Array.from(context.querySelectorAll(selector));
  const reducedMotion = Boolean(w.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
  const coarsePointer = Boolean(w.matchMedia?.('(pointer: coarse)').matches);
  const WASM_CORE = 'AGFzbQEAAAABCwJgAX8Bf2ABfQF9AwMCAAEHDwIEaGFzaAAABGVhc2UAAQozAhoAIAAgAEENdHMiACAAQRF2cyIAIABBBXRzCxYAIAAgAJRDAABAQEMAAABAIACUk5QL';
  const MOTION_KEY = 'kloudz_spatial_motion_v1';
  const initialized = new WeakSet();

  const readPreference = () => {
    try { return localStorage.getItem(MOTION_KEY) || ''; } catch (_) { return ''; }
  };
  const writePreference = (value) => {
    try { localStorage.setItem(MOTION_KEY, value); } catch (_) {}
  };
  const jsHash = (input) => {
    let value = input | 0;
    value ^= value << 13;
    value ^= value >>> 17;
    value ^= value << 5;
    return value | 0;
  };
  const jsEase = (value) => value * value * (3 - (2 * value));
  const decodeBase64 = (value) => Uint8Array.from(w.atob(value), (character) => character.charCodeAt(0));

  const BACKGROUND_SHADER = `
struct Uniforms {
  frame: vec4<f32>,
  viewport: vec4<f32>,
  signals: vec4<f32>,
}

@group(0) @binding(0) var<uniform> u: Uniforms;

struct VertexOutput {
  @builtin(position) position: vec4<f32>,
  @location(0) uv: vec2<f32>,
}

@vertex
fn vertexMain(@builtin(vertex_index) vertexIndex: u32) -> VertexOutput {
  var points = array<vec2<f32>, 3>(
    vec2<f32>(-1.0, -3.0),
    vec2<f32>(-1.0, 1.0),
    vec2<f32>(3.0, 1.0)
  );
  var output: VertexOutput;
  output.position = vec4<f32>(points[vertexIndex], 0.0, 1.0);
  output.uv = (output.position.xy * 0.5) + vec2<f32>(0.5);
  return output;
}

fn hash21(point: vec2<f32>) -> f32 {
  return fract(sin(dot(point, vec2<f32>(127.1, 311.7))) * 43758.5453);
}

fn valueNoise(point: vec2<f32>) -> f32 {
  let cell = floor(point);
  let fraction = fract(point);
  let curve = fraction * fraction * (vec2<f32>(3.0) - (2.0 * fraction));
  let a = hash21(cell);
  let b = hash21(cell + vec2<f32>(1.0, 0.0));
  let c = hash21(cell + vec2<f32>(0.0, 1.0));
  let e = hash21(cell + vec2<f32>(1.0, 1.0));
  return mix(mix(a, b, curve.x), mix(c, e, curve.x), curve.y);
}

@fragment
fn fragmentMain(input: VertexOutput) -> @location(0) vec4<f32> {
  let time = u.frame.x;
  let aspect = u.viewport.z;
  let intensity = u.viewport.w;
  let uv = input.uv;
  var point = (uv - vec2<f32>(0.5)) * vec2<f32>(aspect, 1.0);
  let pointer = u.frame.zw * vec2<f32>(aspect, 1.0);
  let pointerDistance = length(point - pointer);
  let core = exp(-pointerDistance * 3.1) * intensity;
  let drift = vec2<f32>(time * 0.035, -time * 0.023);
  let cloudA = valueNoise((point * 3.1) + drift);
  let cloudB = valueNoise((point * 6.2) - (drift * 1.7));
  let cloud = smoothstep(0.28, 0.88, (cloudA * 0.72) + (cloudB * 0.28));
  let ringDistance = length(point - (pointer * 0.56));
  let ring = (0.5 + (0.5 * sin((ringDistance * 21.0) - (time * 1.12)))) * exp(-ringDistance * 1.8);
  let latticeX = smoothstep(0.975, 1.0, cos((point.x + (time * 0.01)) * 41.0));
  let latticeY = smoothstep(0.982, 1.0, cos((point.y - (time * 0.008)) * 41.0));
  let lattice = (latticeX + latticeY) * 0.055;
  let sweep = 1.0 - smoothstep(0.0, 0.66, abs(point.y + (sin((point.x * 2.8) + (time * 0.3)) * 0.12)));

  let base = vec3<f32>(0.965, 0.976, 0.996);
  let rose = vec3<f32>(0.76, 0.27, 0.48);
  let blue = vec3<f32>(0.30, 0.57, 0.90);
  let mint = vec3<f32>(0.27, 0.72, 0.64);
  var color = mix(base, blue, cloud * 0.19);
  color = mix(color, rose, (ring * 0.13) + (core * 0.21));
  color = mix(color, mint, sweep * cloud * 0.08);
  color += vec3<f32>(lattice);
  color += vec3<f32>(0.055, 0.045, 0.08) * core;
  let edge = 1.0 - smoothstep(0.28, 1.14, length(point * vec2<f32>(0.72, 1.0)));
  return vec4<f32>(color, 0.54 + (edge * 0.30));
}`;

  const COMPUTE_SHADER = `
struct Uniforms {
  frame: vec4<f32>,
  viewport: vec4<f32>,
  signals: vec4<f32>,
}

struct Particle {
  position: vec2<f32>,
  velocity: vec2<f32>,
  seed: f32,
  size: f32,
  orbit: f32,
  padding: f32,
}

@group(0) @binding(0) var<storage, read_write> particles: array<Particle>;
@group(0) @binding(1) var<uniform> u: Uniforms;

@compute @workgroup_size(64)
fn computeMain(@builtin(global_invocation_id) globalId: vec3<u32>) {
  let id = globalId.x;
  if (f32(id) >= u.signals.x) {
    return;
  }
  var particle = particles[id];
  let time = u.frame.x;
  let step = clamp(u.frame.y * 60.0, 0.0, 2.0);
  let pointer = u.frame.zw;
  let phase = (particle.seed * 6.2831853) + (time * (0.12 + (particle.orbit * 0.18)));
  let flow = vec2<f32>(
    -sin((particle.position.y * 3.2) + phase),
    cos((particle.position.x * 3.2) - phase)
  ) * (0.00034 + (particle.orbit * 0.00028));
  let difference = pointer - particle.position;
  let distanceSquared = max(dot(difference, difference), 0.045);
  let attraction = difference * ((0.000018 / distanceSquared) * u.viewport.w);
  particle.velocity = (particle.velocity + flow + attraction) * pow(0.982, step);
  particle.position += particle.velocity * step;

  if (particle.position.x > 1.24) { particle.position.x = -1.24; }
  if (particle.position.x < -1.24) { particle.position.x = 1.24; }
  if (particle.position.y > 1.24) { particle.position.y = -1.24; }
  if (particle.position.y < -1.24) { particle.position.y = 1.24; }
  particles[id] = particle;
}`;

  const PARTICLE_SHADER = `
struct Uniforms {
  frame: vec4<f32>,
  viewport: vec4<f32>,
  signals: vec4<f32>,
}

struct Particle {
  position: vec2<f32>,
  velocity: vec2<f32>,
  seed: f32,
  size: f32,
  orbit: f32,
  padding: f32,
}

@group(0) @binding(0) var<storage, read> particles: array<Particle>;
@group(0) @binding(1) var<uniform> u: Uniforms;

struct ParticleOutput {
  @builtin(position) position: vec4<f32>,
  @location(0) local: vec2<f32>,
  @location(1) seed: f32,
}

@vertex
fn vertexMain(@builtin(vertex_index) vertexIndex: u32) -> ParticleOutput {
  var corners = array<vec2<f32>, 6>(
    vec2<f32>(-1.0, -1.0), vec2<f32>(1.0, -1.0), vec2<f32>(-1.0, 1.0),
    vec2<f32>(-1.0, 1.0), vec2<f32>(1.0, -1.0), vec2<f32>(1.0, 1.0)
  );
  let particleIndex = vertexIndex / 6u;
  let corner = corners[vertexIndex % 6u];
  let particle = particles[particleIndex];
  let aspect = max(u.viewport.z, 0.2);
  let scale = vec2<f32>(particle.size / aspect, particle.size);
  var output: ParticleOutput;
  output.position = vec4<f32>(particle.position + (corner * scale), 0.0, 1.0);
  output.local = corner;
  output.seed = particle.seed;
  return output;
}

@fragment
fn fragmentMain(input: ParticleOutput) -> @location(0) vec4<f32> {
  let distance = length(input.local);
  let body = 1.0 - smoothstep(0.18, 1.0, distance);
  let glow = 1.0 - smoothstep(0.0, 1.0, distance);
  let rose = vec3<f32>(0.82, 0.28, 0.50);
  let blue = vec3<f32>(0.28, 0.62, 0.96);
  let mint = vec3<f32>(0.24, 0.75, 0.64);
  var color = mix(rose, blue, fract(input.seed * 4.73));
  color = mix(color, mint, smoothstep(0.72, 1.0, fract(input.seed * 9.17)) * 0.55);
  let alpha = (body * 0.72) + (glow * 0.16);
  return vec4<f32>(color, alpha);
}`;

  class SpatialEngine {
    constructor(root) {
      this.root = root;
      this.canvas = $('[data-kzw-canvas]', root);
      this.statusNode = $('[data-kzw-status]', root);
      this.gpuNode = $('[data-kzw-gpu]', root);
      this.wasmNode = $('[data-kzw-wasm]', root);
      this.qualityNode = $('[data-kzw-quality-label]', root);
      this.toggle = $('[data-kzw-toggle]', root);
      this.toggleLabel = $('[data-kzw-toggle-label]', root);
      this.requestedQuality = root.dataset.kzwQuality || 'adaptive';
      this.kernel = { hash: jsHash, ease: jsEase };
      this.wasmReady = false;
      this.gpuReady = false;
      this.booted = false;
      this.booting = false;
      this.visible = false;
      this.raf = 0;
      this.lastFrame = 0;
      this.frameTotal = 0;
      this.frameSamples = 0;
      this.degraded = false;
      this.recoveryAttempts = 0;
      this.pointer = { x: 0, y: 0, targetX: 0, targetY: 0, intensity: .22 };
      const preference = readPreference();
      this.paused = reducedMotion || preference === 'paused';
      if (preference === 'running') this.paused = false;
      this.profile = this.chooseProfile();
      this.drawCount = this.profile.particles;
      this.setupInterface();
    }

    chooseProfile() {
      const memory = Number(nav.deviceMemory || 4);
      const cores = Number(nav.hardwareConcurrency || 4);
      const mobile = coarsePointer || w.innerWidth < 760;
      const saveData = Boolean(nav.connection?.saveData);
      if (this.requestedQuality === 'eco' || saveData) {
        return { name: 'Energy saver', particles: mobile ? 448 : 640, dpr: 1, adaptive: false };
      }
      if (this.requestedQuality === 'high') {
        return { name: 'High fidelity', particles: mobile ? 1280 : 2304, dpr: mobile ? 1.25 : 1.65, adaptive: true };
      }
      const constrained = memory <= 4 || cores <= 4 || mobile;
      return constrained
        ? { name: 'Adaptive light', particles: mobile ? 704 : 896, dpr: 1.1, adaptive: true }
        : { name: 'Adaptive high', particles: 1664, dpr: 1.45, adaptive: true };
    }

    setupInterface() {
      this.root.classList.toggle('is-paused', this.paused);
      if (this.toggle) {
        this.toggle.setAttribute('aria-pressed', String(this.paused));
        this.toggleLabel.textContent = this.paused ? 'Resume effects' : 'Pause effects';
        const icon = $('b', this.toggle);
        if (icon) icon.textContent = this.paused ? '▶' : 'Ⅱ';
        this.toggle.addEventListener('click', () => {
          this.paused = !this.paused;
          writePreference(this.paused ? 'paused' : 'running');
          this.root.classList.toggle('is-paused', this.paused);
          this.toggle.setAttribute('aria-pressed', String(this.paused));
          this.toggleLabel.textContent = this.paused ? 'Resume effects' : 'Pause effects';
          if (icon) icon.textContent = this.paused ? '▶' : 'Ⅱ';
          if (this.paused) {
            this.stop();
            if (this.gpuReady) this.renderStatic();
          } else if (!this.booted) {
            this.boot();
          } else {
            this.start();
          }
        });
      }

      this.root.addEventListener('pointermove', (event) => {
        const rect = this.root.getBoundingClientRect();
        const normalizedX = ((event.clientX - rect.left) / Math.max(1, rect.width)) * 2 - 1;
        const normalizedY = -((((event.clientY - rect.top) / Math.max(1, rect.height)) * 2) - 1);
        this.pointer.targetX = Math.max(-1, Math.min(1, normalizedX));
        this.pointer.targetY = Math.max(-1, Math.min(1, normalizedY));
        this.pointer.intensity = 1;
        this.root.style.setProperty('--kzw-gx', `${normalizedX * 7}%`);
        this.root.style.setProperty('--kzw-gy', `${normalizedY * -6}%`);
        this.root.style.setProperty('--kzw-tx', `${normalizedX * 20}px`);
        this.root.style.setProperty('--kzw-ty', `${normalizedY * -15}px`);
      }, { passive: true });
      this.root.addEventListener('pointerleave', () => {
        this.pointer.targetX = 0;
        this.pointer.targetY = 0;
        this.pointer.intensity = .22;
        this.root.style.setProperty('--kzw-gx', '0%');
        this.root.style.setProperty('--kzw-gy', '0%');
        this.root.style.setProperty('--kzw-tx', '0px');
        this.root.style.setProperty('--kzw-ty', '0px');
      }, { passive: true });

      if ('IntersectionObserver' in w) {
        this.visibilityObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.target !== this.root) return;
            this.visible = entry.isIntersecting && entry.intersectionRatio > .03;
            if (this.visible) {
              if (!this.booted && !this.booting) this.boot();
              else this.start();
            } else {
              this.stop();
            }
          });
        }, { rootMargin: '180px 0px', threshold: [0, .03, .2] });
        this.visibilityObserver.observe(this.root);
      } else {
        this.visible = true;
        w.setTimeout(() => this.boot(), 0);
      }

      d.addEventListener('visibilitychange', () => d.hidden ? this.stop() : this.start());
      if ('ResizeObserver' in w) {
        this.resizeObserver = new ResizeObserver(() => this.resize());
        this.resizeObserver.observe(this.root);
      } else {
        w.addEventListener('resize', () => this.resize(), { passive: true });
      }
      const ageGate = $('#KloudzAgeGate');
      if (ageGate) {
        this.gateObserver = new MutationObserver(() => this.start());
        this.gateObserver.observe(ageGate, { attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
      }
    }

    setStatus(message) {
      if (this.statusNode) this.statusNode.textContent = message;
    }

    fallbackCode(error) {
      const detail = String(error?.message || error?.reason || error || '').toLowerCase();
      if (detail.includes('adapter')) return 'adapter-unavailable';
      if (detail.includes('shader')) return 'shader-validation';
      if (detail.includes('pipeline')) return 'pipeline-creation';
      if (detail.includes('context')) return 'canvas-context';
      if (detail.includes('device')) return 'device-unavailable';
      return 'runtime-unavailable';
    }

    isAgeGateBlocking() {
      const gate = $('#KloudzAgeGate');
      if (!gate) return false;
      const style = w.getComputedStyle(gate);
      return !gate.hidden && style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity || 1) > .02;
    }

    async initKernel() {
      if (typeof w.WebAssembly !== 'object') {
        this.root.dataset.kzwWasm = 'unsupported';
        if (this.wasmNode) this.wasmNode.textContent = 'Compatibility kernel';
        return;
      }
      try {
        const bytes = decodeBase64(WASM_CORE);
        if (!w.WebAssembly.validate(bytes)) throw new Error('WebAssembly validation failed');
        const source = await w.WebAssembly.instantiate(bytes);
        const exports = source.instance.exports;
        if (typeof exports.hash !== 'function' || typeof exports.ease !== 'function') throw new Error('Missing kernel exports');
        this.kernel = { hash: exports.hash, ease: exports.ease };
        this.wasmReady = true;
        this.root.dataset.kzwWasm = 'ready';
        if (this.wasmNode) this.wasmNode.textContent = 'WebAssembly online';
      } catch (_) {
        this.root.dataset.kzwWasm = 'fallback';
        if (this.wasmNode) this.wasmNode.textContent = 'Compatibility kernel';
      }
    }

    async boot() {
      if (this.booting || this.booted) return;
      this.booting = true;
      this.setStatus('Starting WebAssembly motion kernel…');
      await this.initKernel();
      if (!w.isSecureContext || !nav.gpu || !this.canvas) {
        this.root.dataset.kzwGpu = 'unsupported';
        this.root.dataset.kzwMode = this.wasmReady ? 'wasm-css' : 'css';
        this.root.classList.add('is-fallback');
        if (this.gpuNode) this.gpuNode.textContent = 'CSS fallback active';
        if (this.qualityNode) this.qualityNode.textContent = 'Universal';
        this.setStatus(this.wasmReady ? 'WebAssembly online · visual fallback active' : 'Universal visual fallback active');
        this.booted = true;
        this.booting = false;
        return;
      }
      try {
        await this.initGpu();
        this.booted = true;
        this.root.classList.add('is-webgpu');
        this.root.classList.remove('is-fallback');
        this.root.dataset.kzwGpu = 'ready';
        this.root.dataset.kzwMode = this.wasmReady ? 'webgpu-wasm' : 'webgpu-js';
        if (this.gpuNode) this.gpuNode.textContent = 'WebGPU active';
        if (this.qualityNode) this.qualityNode.textContent = `${this.profile.name} · ${this.drawCount.toLocaleString()}`;
        this.setStatus(`${this.wasmReady ? 'WebGPU + WebAssembly' : 'WebGPU'} online · ${this.drawCount.toLocaleString()} particles`);
        this.renderStatic();
        this.start();
      } catch (error) {
        this.activateFallback('Graphics fallback active', error);
      } finally {
        this.booting = false;
      }
    }

    async validateShader(module, label) {
      if (typeof module.getCompilationInfo !== 'function') return;
      const info = await module.getCompilationInfo();
      const errors = info.messages.filter((message) => message.type === 'error');
      if (errors.length) throw new Error(`${label}: ${errors.map((message) => message.message).join('; ')}`);
    }

    async initGpu() {
      this.adapter = await nav.gpu.requestAdapter({ powerPreference: this.requestedQuality === 'eco' ? 'low-power' : 'high-performance' });
      if (!this.adapter) throw new Error('No WebGPU adapter');
      this.device = await this.adapter.requestDevice();
      this.context = this.canvas.getContext('webgpu');
      if (!this.context) throw new Error('No WebGPU canvas context');
      this.format = nav.gpu.getPreferredCanvasFormat();
      this.context.configure({ device: this.device, format: this.format, alphaMode: 'premultiplied' });

      const backgroundModule = this.device.createShaderModule({ label: 'Kloudz nebula shader', code: BACKGROUND_SHADER });
      const computeModule = this.device.createShaderModule({ label: 'Kloudz particle compute shader', code: COMPUTE_SHADER });
      const particleModule = this.device.createShaderModule({ label: 'Kloudz particle render shader', code: PARTICLE_SHADER });
      await Promise.all([
        this.validateShader(backgroundModule, 'Nebula shader'),
        this.validateShader(computeModule, 'Compute shader'),
        this.validateShader(particleModule, 'Particle shader')
      ]);

      const backgroundDescriptor = {
        label: 'Kloudz nebula pipeline',
        layout: 'auto',
        vertex: { module: backgroundModule, entryPoint: 'vertexMain' },
        fragment: { module: backgroundModule, entryPoint: 'fragmentMain', targets: [{ format: this.format }] },
        primitive: { topology: 'triangle-list' }
      };
      const computeDescriptor = {
        label: 'Kloudz particle compute pipeline',
        layout: 'auto',
        compute: { module: computeModule, entryPoint: 'computeMain' }
      };
      const particleDescriptor = {
        label: 'Kloudz particle render pipeline',
        layout: 'auto',
        vertex: { module: particleModule, entryPoint: 'vertexMain' },
        fragment: {
          module: particleModule,
          entryPoint: 'fragmentMain',
          targets: [{
            format: this.format,
            blend: {
              color: { srcFactor: 'src-alpha', dstFactor: 'one', operation: 'add' },
              alpha: { srcFactor: 'one', dstFactor: 'one-minus-src-alpha', operation: 'add' }
            }
          }]
        },
        primitive: { topology: 'triangle-list' }
      };
      const makeRender = (descriptor) => this.device.createRenderPipelineAsync
        ? this.device.createRenderPipelineAsync(descriptor)
        : Promise.resolve(this.device.createRenderPipeline(descriptor));
      const makeCompute = (descriptor) => this.device.createComputePipelineAsync
        ? this.device.createComputePipelineAsync(descriptor)
        : Promise.resolve(this.device.createComputePipeline(descriptor));
      [this.backgroundPipeline, this.computePipeline, this.particlePipeline] = await Promise.all([
        makeRender(backgroundDescriptor),
        makeCompute(computeDescriptor),
        makeRender(particleDescriptor)
      ]);

      const usage = w.GPUBufferUsage;
      if (!usage) throw new Error('WebGPU buffer constants unavailable');
      this.uniformBuffer = this.device.createBuffer({ label: 'Kloudz frame uniforms', size: 48, usage: usage.UNIFORM | usage.COPY_DST });
      this.particleBuffer = this.device.createBuffer({ label: 'Kloudz particle field', size: this.profile.particles * 32, usage: usage.STORAGE | usage.COPY_DST });
      this.seedParticles();

      this.backgroundGroup = this.device.createBindGroup({
        layout: this.backgroundPipeline.getBindGroupLayout(0),
        entries: [{ binding: 0, resource: { buffer: this.uniformBuffer } }]
      });
      this.computeGroup = this.device.createBindGroup({
        layout: this.computePipeline.getBindGroupLayout(0),
        entries: [
          { binding: 0, resource: { buffer: this.particleBuffer } },
          { binding: 1, resource: { buffer: this.uniformBuffer } }
        ]
      });
      this.particleGroup = this.device.createBindGroup({
        layout: this.particlePipeline.getBindGroupLayout(0),
        entries: [
          { binding: 0, resource: { buffer: this.particleBuffer } },
          { binding: 1, resource: { buffer: this.uniformBuffer } }
        ]
      });
      this.gpuReady = true;
      this.resize();

      this.device.lost.then((info) => {
        this.gpuReady = false;
        this.stop();
        this.root.classList.remove('is-webgpu');
        if (info.reason !== 'destroyed' && this.recoveryAttempts < 1) {
          this.recoveryAttempts += 1;
          this.setStatus('Restoring the visual engine…');
          w.setTimeout(() => this.recoverGpu(), 900);
        } else {
          this.activateFallback('Visual engine moved to fallback mode');
        }
      });
      this.device.addEventListener?.('uncapturederror', (event) => this.activateFallback('Visual engine moved to fallback mode', event.error));
    }

    seedParticles() {
      const values = new Float32Array(this.profile.particles * 8);
      const normalize = (value) => (value >>> 0) / 4294967295;
      for (let index = 0; index < this.profile.particles; index += 1) {
        const offset = index * 8;
        const a = normalize(this.kernel.hash((index + 1) ^ 0x45d9f3b));
        const b = normalize(this.kernel.hash((index + 1) ^ 0x27d4eb2d));
        const c = normalize(this.kernel.hash((index + 1) ^ 0x165667b1));
        const e = normalize(this.kernel.hash((index + 1) ^ 0x6c8e9cf5));
        values[offset] = (a * 2.42) - 1.21;
        values[offset + 1] = (b * 2.42) - 1.21;
        values[offset + 2] = (c - .5) * .0011;
        values[offset + 3] = (e - .5) * .0011;
        values[offset + 4] = c;
        values[offset + 5] = .0045 + (e * .0135);
        values[offset + 6] = a;
        values[offset + 7] = 0;
      }
      this.device.queue.writeBuffer(this.particleBuffer, 0, values);
    }

    resize() {
      if (!this.canvas || !this.gpuReady) return;
      const rect = this.root.getBoundingClientRect();
      const devicePixelRatio = Math.min(Number(w.devicePixelRatio || 1), this.profile.dpr);
      const width = Math.max(2, Math.min(1920, Math.round(rect.width * devicePixelRatio)));
      const height = Math.max(2, Math.min(1080, Math.round(rect.height * devicePixelRatio)));
      if (this.canvas.width !== width || this.canvas.height !== height) {
        this.canvas.width = width;
        this.canvas.height = height;
      }
    }

    start() {
      if (!this.gpuReady || this.paused || !this.visible || d.hidden || this.isAgeGateBlocking() || this.raf) return;
      this.lastFrame = w.performance.now();
      this.raf = w.requestAnimationFrame((time) => this.frame(time));
    }

    stop() {
      if (this.raf) w.cancelAnimationFrame(this.raf);
      this.raf = 0;
    }

    renderStatic() {
      if (!this.gpuReady) return;
      try {
        this.draw(w.performance.now(), 1 / 60);
      } catch (error) {
        this.activateFallback('Visual engine moved to fallback mode', error);
      }
    }

    frame(time) {
      this.raf = 0;
      if (!this.gpuReady || this.paused || !this.visible || d.hidden || this.isAgeGateBlocking()) return;
      const delta = Math.min(.034, Math.max(.001, (time - this.lastFrame) / 1000));
      this.lastFrame = time;
      try {
        this.draw(time, delta);
      } catch (error) {
        this.activateFallback('Visual engine moved to fallback mode', error);
        return;
      }
      this.frameTotal += delta;
      this.frameSamples += 1;
      if (this.frameSamples >= 120) {
        const average = this.frameTotal / this.frameSamples;
        if (this.profile.adaptive && average > .023 && !this.degraded) this.degradeQuality();
        this.frameTotal = 0;
        this.frameSamples = 0;
      }
      this.raf = w.requestAnimationFrame((nextTime) => this.frame(nextTime));
    }

    draw(time, delta) {
      const smoothing = this.kernel.ease(Math.min(1, delta * 8.5));
      this.pointer.x += (this.pointer.targetX - this.pointer.x) * smoothing;
      this.pointer.y += (this.pointer.targetY - this.pointer.y) * smoothing;
      const scrollRange = Math.max(1, d.documentElement.scrollHeight - w.innerHeight);
      const scroll = Math.max(0, Math.min(1, w.scrollY / scrollRange));
      const width = Math.max(1, this.canvas.width);
      const height = Math.max(1, this.canvas.height);
      const uniforms = new Float32Array([
        time / 1000, delta, this.pointer.x, this.pointer.y,
        width, height, width / height, this.pointer.intensity,
        this.drawCount, scroll, this.degraded ? 0 : 1, 0
      ]);
      this.device.queue.writeBuffer(this.uniformBuffer, 0, uniforms);
      const encoder = this.device.createCommandEncoder({ label: 'Kloudz spatial frame' });
      const computePass = encoder.beginComputePass({ label: 'Kloudz particle motion' });
      computePass.setPipeline(this.computePipeline);
      computePass.setBindGroup(0, this.computeGroup);
      computePass.dispatchWorkgroups(Math.ceil(this.drawCount / 64));
      computePass.end();
      const renderPass = encoder.beginRenderPass({
        label: 'Kloudz spatial render',
        colorAttachments: [{
          view: this.context.getCurrentTexture().createView(),
          clearValue: { r: 0, g: 0, b: 0, a: 0 },
          loadOp: 'clear',
          storeOp: 'store'
        }]
      });
      renderPass.setPipeline(this.backgroundPipeline);
      renderPass.setBindGroup(0, this.backgroundGroup);
      renderPass.draw(3);
      renderPass.setPipeline(this.particlePipeline);
      renderPass.setBindGroup(0, this.particleGroup);
      renderPass.draw(this.drawCount * 6);
      renderPass.end();
      this.device.queue.submit([encoder.finish()]);
    }

    degradeQuality() {
      this.degraded = true;
      this.drawCount = Math.max(448, Math.floor(this.drawCount * .68));
      this.profile.dpr = Math.max(1, this.profile.dpr * .78);
      this.root.dataset.kzwAdapted = 'true';
      this.resize();
      if (this.qualityNode) this.qualityNode.textContent = `Adaptive balanced · ${this.drawCount.toLocaleString()}`;
      this.setStatus(`${this.wasmReady ? 'WebGPU + WebAssembly' : 'WebGPU'} online · performance balanced`);
    }

    async recoverGpu() {
      try {
        await this.initGpu();
        this.root.classList.add('is-webgpu');
        this.root.dataset.kzwGpu = 'ready';
        if (this.gpuNode) this.gpuNode.textContent = 'WebGPU restored';
        this.setStatus('Visual engine restored');
        this.renderStatic();
        this.start();
      } catch (error) {
        this.activateFallback('Visual engine moved to fallback mode', error);
      }
    }

    activateFallback(message, error) {
      this.gpuReady = false;
      this.stop();
      this.root.classList.remove('is-webgpu');
      this.root.classList.add('is-fallback');
      this.root.dataset.kzwGpu = 'fallback';
      this.root.dataset.kzwMode = this.wasmReady ? 'wasm-css' : 'css';
      this.root.dataset.kzwFallback = this.fallbackCode(error);
      if (this.gpuNode) this.gpuNode.textContent = 'CSS fallback active';
      if (this.qualityNode) this.qualityNode.textContent = 'Universal';
      this.setStatus(message);
    }
  }

  const initialize = (root) => {
    if (!root || initialized.has(root)) return;
    initialized.add(root);
    const engine = new SpatialEngine(root);
    root.kloudzSpatialEngine = engine;
  };
  $$('[data-kzw-engine]').forEach(initialize);
  d.addEventListener('shopify:section:load', (event) => $$('[data-kzw-engine]', event.target).forEach(initialize));
  d.documentElement.classList.add('kzw-capable');
})();
